import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, BookOpen, FileText, GraduationCap } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import ProgramDetailModal, {
  type ProgramTemplate,
} from '@/components/programs/ProgramDetailModal'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Wrapper returned by GET /api/v1/program-templates
 */
interface ProgramTemplatesResponse {
  data: ProgramTemplate[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns a country flag emoji for a given country name.
 */
function countryFlag(country?: string): string {
  if (!country) return '🌐'
  const map: Record<string, string> = {
    'United States': '🇺🇸',
    USA: '🇺🇸',
    'United Kingdom': '🇬🇧',
    UK: '🇬🇧',
    Canada: '🇨🇦',
    France: '🇫🇷',
    Germany: '🇩🇪',
    Spain: '🇪🇸',
    Singapore: '🇸🇬',
    India: '🇮🇳',
    Australia: '🇦🇺',
    Switzerland: '🇨🇭',
  }
  return map[country] ?? '🌐'
}

// ---------------------------------------------------------------------------
// ProgramCard
// ---------------------------------------------------------------------------

interface ProgramCardProps {
  program: ProgramTemplate
  onViewDetails: (program: ProgramTemplate) => void
}

/**
 * ProgramCard — compact card shown in the programs grid.
 */
function ProgramCard({ program, onViewDetails }: ProgramCardProps) {
  const essayCount = program.essayCount ?? program.essays?.length ?? 0
  const testRequired = program.testRequired ?? [
    ...(program.gmatAccepted ? ['GMAT'] : []),
    ...(program.greAccepted ? ['GRE'] : []),
  ]

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
      {/* University + program name */}
      <div className="min-w-0">
        <h3 className="font-semibold text-foreground leading-snug line-clamp-2">
          {program.universityName}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <p className="text-sm text-muted-foreground leading-snug">{program.programName}</p>
          {program.degree && (
            <span className="inline-flex items-center rounded-full border border-brand-blue/30 bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue">
              {program.degree}
            </span>
          )}
        </div>
      </div>

      {/* Country */}
      {program.country && (
        <p className="mt-2 text-xs text-muted-foreground">
          {countryFlag(program.country)} {program.country}
        </p>
      )}

      {/* Key stats row */}
      <div className="mt-4 flex flex-wrap gap-2">
        {program.lorCount !== undefined && (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
            <BookOpen className="h-3 w-3 text-muted-foreground" />
            {program.lorCount} LOR{program.lorCount !== 1 ? 's' : ''}
          </span>
        )}
        {essayCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
            <FileText className="h-3 w-3 text-muted-foreground" />
            {essayCount} Essay{essayCount !== 1 ? 's' : ''}
          </span>
        )}
        {testRequired.map((test) => (
          <span
            key={test}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
              test === 'GMAT'
                ? 'bg-brand-purple/10 text-brand-purple'
                : 'bg-brand-green/10 text-brand-green',
            )}
          >
            <GraduationCap className="h-3 w-3" />
            {test}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-border">
        <button
          type="button"
          onClick={() => onViewDetails(program)}
          className="w-full rounded-md border border-brand-blue/40 bg-brand-blue/5 px-3 py-1.5 text-xs font-semibold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          View Details
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProgramsPage
// ---------------------------------------------------------------------------

/**
 * ProgramsPage — directory of seeded MBA program templates.
 *
 * Fetches from GET /api/v1/program-templates with an optional ?search= param.
 * Search is debounced 300 ms to avoid hammering the API on every keystroke.
 *
 * Selecting a card opens ProgramDetailModal where the user can add the
 * program to their applications.
 */
export default function ProgramsPage() {
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProgram, setSelectedProgram] = useState<ProgramTemplate | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounce the search input: update searchQuery 300 ms after the user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchQuery(inputValue.trim())
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [inputValue])

  // Build the API path — omit ?search= on empty query to fetch all programs
  const apiPath = searchQuery
    ? `/v1/program-templates?search=${encodeURIComponent(searchQuery)}`
    : '/v1/program-templates'

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<ProgramTemplatesResponse>({
    queryKey: ['program-templates', searchQuery],
    queryFn: () => api.get<ProgramTemplatesResponse>(apiPath),
    staleTime: 5 * 60_000, // 5 minutes — seeded data changes infrequently
  })

  const programs = data?.data ?? []

  function handleViewDetails(program: ProgramTemplate) {
    setSelectedProgram(program)
    setModalOpen(true)
  }

  function handleModalClose() {
    setModalOpen(false)
    // Keep selectedProgram mounted until the modal animation completes
    setTimeout(() => setSelectedProgram(null), 200)
  }

  return (
    <>
      <div className="flex flex-col gap-6 p-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">MBA Programs</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Browse {programs.length > 0 ? `${programs.length} ` : ''}programs. Add any to your applications.
            </p>
          </div>

          {/* Search input */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search universities or programs..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Search programs"
            />
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div
              className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue border-t-transparent"
              role="status"
              aria-label="Loading programs…"
            />
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-5 py-4 text-sm text-destructive">
            {error instanceof Error
              ? error.message
              : 'Failed to load programs. Please try again.'}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && programs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <GraduationCap className="h-12 w-12 text-muted-foreground/40" />
            <p className="mt-4 text-base font-medium text-muted-foreground">
              {searchQuery ? 'No programs match your search.' : 'No programs found.'}
            </p>
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setInputValue('')
                  setSearchQuery('')
                }}
                className="mt-3 text-sm text-brand-blue hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {/* Programs grid */}
        {!isLoading && !isError && programs.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
      </div>

      {/* Program detail modal */}
      <ProgramDetailModal
        program={selectedProgram}
        isOpen={modalOpen}
        onClose={handleModalClose}
      />
    </>
  )
}
